import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const ROUTE_FOCUS_SELECTOR = "[data-route-focus-target], #main-content";
const MAX_ROUTE_FOCUS_ATTEMPTS = 8;
const ROUTE_FOCUS_RETRY_MS = 50;

function getHashTarget(hash: string): HTMLElement | null {
  if (!hash.startsWith("#") || hash.length <= 1) return null;
  try {
    return document.getElementById(decodeURIComponent(hash.slice(1)));
  } catch {
    return document.getElementById(hash.slice(1));
  }
}

function ensureProgrammaticFocusTarget(target: HTMLElement) {
  if (!target.hasAttribute("tabindex")) {
    target.setAttribute("tabindex", "-1");
  }
}

export function RouteAccessibility() {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    let timer = 0;

    const focusRoute = (attempt: number) => {
      if (cancelled) return;
      const hashTarget = getHashTarget(location.hash);
      if (hashTarget) {
        ensureProgrammaticFocusTarget(hashTarget);
        hashTarget.focus({ preventScroll: false });
        return;
      }

      if (attempt === 0) {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
      const target = document.querySelector<HTMLElement>(ROUTE_FOCUS_SELECTOR);
      if (!target) {
        if (attempt < MAX_ROUTE_FOCUS_ATTEMPTS) {
          timer = window.setTimeout(() => focusRoute(attempt + 1), ROUTE_FOCUS_RETRY_MS);
        }
        return;
      }
      ensureProgrammaticFocusTarget(target);
      target.focus({ preventScroll: true });
    };

    timer = window.setTimeout(() => focusRoute(0), 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [location.hash, location.pathname, location.search]);

  return null;
}
